import { assertNever } from "@@/shared/assert-never";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import type { DiscountCondition } from "./discount-condition";
import type { DiscountConditionValidator } from "./discount-condition-validator";
import {
    getItemsConditionValidator,
    isItemsCondition,
} from "./items-condition";
import {
    getMinNumberOfItemsConditionValidator,
    isMinNumberOfItemsCondition,
} from "./min-number-of-items-condition";
import {
    getProviderConditionValidator,
    isProviderCondition,
} from "./provider-condition";
import {
    getResourceConditionValidator,
    isResourceCondition,
} from "./resource-condition";
import {
    getTimeOfDayConditionValidator,
    isTimeOfDayCondition,
} from "./time-of-day-condition";
import {
    getTimeRangeConditionValidator,
    isTimeRangeCondition,
} from "./time-range-condition";
import {
    getValueConditionValidator,
    isValueCondition,
} from "./value-condition";
import {
    getWeekdayConditionValidator,
    isWeekdayCondition,
} from "./week-day-condition";

const getDiscountConditionValidator = <
    TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2,
    Condition extends DiscountCondition = DiscountCondition,
>(
    discountCondition: Condition,
): DiscountConditionValidator<TOrderItem> => {
    if (isProviderCondition(discountCondition)) {
        return getProviderConditionValidator<TOrderItem>(discountCondition);
    }
    if (isValueCondition(discountCondition)) {
        return getValueConditionValidator<TOrderItem>(discountCondition);
    }
    if (isItemsCondition(discountCondition)) {
        return getItemsConditionValidator<TOrderItem>(discountCondition);
    }
    if (isTimeRangeCondition(discountCondition)) {
        return getTimeRangeConditionValidator<TOrderItem>(discountCondition);
    }
    if (isMinNumberOfItemsCondition(discountCondition)) {
        return getMinNumberOfItemsConditionValidator<TOrderItem>(
            discountCondition,
        );
    }
    if (isWeekdayCondition(discountCondition)) {
        return getWeekdayConditionValidator<TOrderItem>(discountCondition);
    }
    if (isResourceCondition(discountCondition)) {
        return getResourceConditionValidator<TOrderItem>(discountCondition);
    }
    if (isTimeOfDayCondition(discountCondition)) {
        return getTimeOfDayConditionValidator<TOrderItem>(discountCondition);
    }
    assertNever(discountCondition);
    throw new Error("Invalid discount condition type");
};

export { getDiscountConditionValidator };
